import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/application/stores/auth.store'
import { publicLayoutRoute } from '@/ui/pages/public'
import type { User } from '@/domains/types'
import { CredentialsSection } from './components/CredentialsSection'
import { LoginHeader } from './components/LoginHeader'
import { TotpSection } from './components/TotpSection'

export const publicLoginRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'login',
  component: PublicLoginPage,
})

const API_BASE = import.meta.env.VITE_API_URL || '/api'

/**
 * After a successful credentials or TOTP login, determine where to send
 * the user — mirroring the logic in the OAuth callback handler:
 *
 *  - Streamer / admin / moderator → /manage/dashboard
 *  - Viewer who is author or team member of at least one investigation
 *    → /manage/profile (they have something to manage)
 *  - Pure viewer with no investigations → / (public homepage)
 */
async function resolvePostLoginRedirect(user: User, token: string): Promise<string> {
  if (user.role === 'streamer' || user.role === 'moderator' || user.role === 'admin') {
    return '/manage/dashboard'
  }
  try {
    const result = await fetch(`${API_BASE}/investigations/my`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (result.ok) {
      const data: unknown[] = await result.json()
      if (Array.isArray(data) && data.length > 0) return '/manage/profile'
    }
  } catch {
    // Ignore — fall through to public homepage
  }
  return '/'
}

/**
 * The single login page for all users (public and privileged alike).
 * Owns the page-level `tempToken` (which doubles as the step indicator between
 * the credentials and TOTP sections) and the post-authentication redirect logic.
 */
export function PublicLoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, login } = useAuthStore()

  const [tempToken, setTempToken] = useState<string | null>(null)
  const step: 'credentials' | 'totp' = tempToken ? 'totp' : 'credentials'

  // Already authenticated — no need to log in again; send them to the right place.
  useEffect(() => {
    if (user) {
      const isPrivileged =
        user.role === 'streamer' || user.role === 'moderator' || user.role === 'admin'
      navigate({ to: isPrivileged ? '/manage/dashboard' : '/manage/profile' })
    }
  }, [user, navigate])

  const handleAuthenticated = async (
    apiUser: Omit<User, 'createdAt' | 'updatedAt'>,
    accessToken: string,
    refreshToken: string,
  ) => {
    const timestamp = new Date().toISOString()
    const fullUser: User = { ...apiUser, createdAt: timestamp, updatedAt: timestamp }
    login(fullUser, accessToken, refreshToken)
    const dest = await resolvePostLoginRedirect(fullUser, accessToken)
    void navigate({ to: dest })
  }

  return (
    <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <LoginHeader step={step} />

        {step === 'credentials' ? (
          <CredentialsSection
            onAuthenticated={handleAuthenticated}
            onTwoFactorRequired={setTempToken}
          />
        ) : (
          <TotpSection
            tempToken={tempToken!}
            onAuthenticated={handleAuthenticated}
            onBack={() => setTempToken(null)}
          />
        )}

        {step === 'credentials' && (
          <p className="text-center text-sm text-muted-foreground">
            {t('auth.no_account')}{' '}
            <Link to="/register" className="text-primary hover:underline">
              {t('auth.register_link')}
            </Link>
          </p>
        )}
      </div>
    </div>
  )
}
